import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Grid, IconButton, Tooltip, TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel, CircularProgress, Skeleton, Chip, Divider } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbook } from '../../contexts/LogbookContext';
import LogTheme from './LogTheme';
import { getAuthHeaders, getImageDisplayUrl } from '../../utils/api';

const ServiceLogs = () => {
  const { userId } = useAuth();
  const { currentLogbookId } = useLogbook();
  const [serviceLogs, setServiceLogs] = useState([]);
  const [sortOrder, setSortOrder] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [boatFilter, setBoatFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const url = `${process.env.REACT_APP_SERVER_URL}/api/servicelogs/${userId}${currentLogbookId ? `?logbookId=${encodeURIComponent(currentLogbookId)}` : ''}`;
    fetch(url, {
      headers: getAuthHeaders()
    })
      .then(response => {
        if (!response.ok) {
          if (response.status === 401) {
            console.error('401: Inte autentiserad. Logga in igen.');
            return [];
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (!data || !Array.isArray(data)) {
          setServiceLogs([]);
          return;
        }
        const sortedLogs = data.sort((a, b) => {
          if (sortOrder === 'newest') {
            return new Date(b.date) - new Date(a.date);
          } else {
            return new Date(a.date) - new Date(b.date);
          }
        });
        setServiceLogs(sortedLogs);
      })
      .catch(error => {
        console.error('Error:', error);
        setServiceLogs([]);
      })
      .finally(() => setLoading(false));
  }, [userId, sortOrder, currentLogbookId]);

  // Get unique boats from service logs
  const availableBoats = useMemo(() => {
    const boats = serviceLogs
      .map(log => log.boatNameModel)
      .filter(boat => boat != null && boat.trim() !== '')
      .filter((boat, index, self) => self.indexOf(boat) === index)
      .sort();
    return boats;
  }, [serviceLogs]);

  // Filter and search service logs
  const filteredLogs = useMemo(() => {
    let filtered = [...serviceLogs];

    // Boat filter
    if (boatFilter) {
      filtered = filtered.filter(log => log.boatNameModel === boatFilter);
    }

    // Search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log => 
        (log.title && log.title.toLowerCase().includes(query)) ||
        (log.boatNameModel && log.boatNameModel.toLowerCase().includes(query)) ||
        (log.servicePersonOrCompany && log.servicePersonOrCompany.toLowerCase().includes(query)) ||
        (log.serviceType && log.serviceType.toLowerCase().includes(query)) ||
        (log.comments && log.comments.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [serviceLogs, boatFilter, searchQuery]);

  // Funktion för att växla sorteringsordningen
  const toggleSortOrder = () => {
    setSortOrder(prevOrder => (prevOrder === 'newest' ? 'oldest' : 'newest'));
  };

  // Funktion för att växla mellan grid och list vy
  const toggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'grid' ? 'list' : 'grid');
  };


  return (
    <LogTheme>
      <Box padding={{ xs: 2, md: 4 }} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2} className="no-print">
          <Typography variant="h1">Serviceloggar</Typography>
          <Button component={Link} to="/createservicelog" variant="contained" color="primary">
            Ny servicelogg
          </Button>
        </Box>

        {/* Print Header - only visible when printing */}
        <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
          <Typography variant="h1" gutterBottom>
            Service
          </Typography>
          {boatFilter && (
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Filtrerat på båt: {boatFilter}
            </Typography>
          )}
          {searchQuery && (
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Sökterm: {searchQuery}
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Totalt: {filteredLogs.length} servicelogg{filteredLogs.length !== 1 ? 'ar' : ''}
          </Typography>
        </Box>

        {/* Filters and Search */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap" className="no-print">
          <TextField
            placeholder="Sök serviceloggar..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: '200px' }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Båt</InputLabel>
            <Select
              value={boatFilter}
              label="Båt"
              onChange={(e) => setBoatFilter(e.target.value)}
            >
              <MenuItem value="">Alla båtar</MenuItem>
              {availableBoats.map(boat => (
                <MenuItem key={boat} value={boat}>{boat}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Controls */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2} className="no-print">
          <Box display="flex" gap={1} alignItems="center">
            {filteredLogs.length > 0 && (
              <Chip label={`${filteredLogs.length} servicelogg${filteredLogs.length !== 1 ? 'ar' : ''}`} />
            )}
            {(boatFilter || searchQuery) && (
              <Button
                size="small"
                onClick={() => {
                  setBoatFilter('');
                  setSearchQuery('');
                }}
                sx={{
                  backgroundColor: '#E3F2FD',
                  color: '#1565C0',
                  '&:hover': {
                    backgroundColor: '#BBDEFB',
                  },
                }}
              >
                Rensa filter
              </Button>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title={viewMode === 'grid' ? 'Visa som lista' : 'Visa som rutnät'}>
              <IconButton 
                onClick={toggleViewMode} 
                color="primary"
                sx={{ border: '1px solid', borderColor: 'primary.main' }}
              >
                {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
              </IconButton>
            </Tooltip>
            <Button 
              onClick={toggleSortOrder}
              sx={{
                backgroundColor: '#E3F2FD',
                color: '#1565C0',
                border: '1px solid #BBDEFB',
                '&:hover': {
                  backgroundColor: '#BBDEFB',
                  border: '1px solid #90CAF9',
                },
              }}
            >
              Sortera {sortOrder === 'newest' ? '↓' : '↑'}
            </Button>
          </Box>
        </Box>

        {/* Loading State */}
        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" height={40} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : filteredLogs.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="h6" align="center" color="text.secondary">
                {serviceLogs.length === 0 ? 'Inga serviceloggar hittades' : 'Inga serviceloggar matchar dina filter'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Screen view - Grid */}
            {viewMode === 'grid' && (
              <Grid container spacing={2} className="no-print">
                {filteredLogs.map(serviceLog => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={serviceLog._id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        {serviceLog.boatNameModel && (
                          <Chip label={serviceLog.boatNameModel} size="small" color="primary" sx={{ mb: 1, alignSelf: 'flex-start' }} />
                        )}
                        <Typography variant="h5" component="h2" gutterBottom sx={{ flexGrow: 1 }}>
                          {serviceLog.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {new Date(serviceLog.date).toLocaleDateString('sv-SE')}
                        </Typography>
                        {serviceLog.servicePersonOrCompany && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {serviceLog.servicePersonOrCompany}
                          </Typography>
                        )}
                        {serviceLog.comments && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 2, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {serviceLog.comments}
                          </Typography>
                        )}
                        <Button 
                          component={Link} 
                          to={`/showservicelog/${serviceLog._id}`} 
                          variant="contained" 
                          color="primary"
                          fullWidth
                        >
                          Visa
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Screen view - List */}
            {viewMode === 'list' && (
              <Box className="no-print">
                {filteredLogs.map(serviceLog => (
                  <Card key={serviceLog._id} sx={{ marginBottom: 2, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                        <Box flexGrow={1}>
                          {serviceLog.boatNameModel && (
                            <Chip label={serviceLog.boatNameModel} size="small" color="primary" sx={{ mb: 1 }} />
                          )}
                          <Typography variant="h4" gutterBottom>{serviceLog.title}</Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {new Date(serviceLog.date).toLocaleDateString('sv-SE')}
                          </Typography>
                          {serviceLog.servicePersonOrCompany && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {serviceLog.servicePersonOrCompany}
                            </Typography>
                          )}
                          {serviceLog.comments && (
                            <Typography variant="body1" sx={{ mt: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {serviceLog.comments}
                            </Typography>
                          )}
                        </Box>
                        <Button component={Link} to={`/showservicelog/${serviceLog._id}`} variant="contained" color="primary">
                          Visa
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Print view - always shows full details in list format */}
            <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
              {filteredLogs.map((serviceLog) => (
                <Card key={serviceLog._id} sx={{ marginBottom: 3, pageBreakInside: 'avoid' }}>
                  <CardContent>
                    <Box mb={2}>
                      {serviceLog.boatNameModel && (
                        <Chip label={serviceLog.boatNameModel} size="small" color="primary" sx={{ mb: 1 }} />
                      )}
                      <Typography variant="h3" gutterBottom>{serviceLog.title}</Typography>
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        {new Date(serviceLog.date).toLocaleDateString('sv-SE')}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Båtens namn/modell
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {serviceLog.boatNameModel || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Motor/typ
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {serviceLog.engineType || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Serviceperson eller företag
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {serviceLog.servicePersonOrCompany || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Typ av service/underhåll
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {serviceLog.serviceType || '-'}
                        </Typography>
                      </Grid>
                      {serviceLog.engineService && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Motorservice
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.engineService}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.sailAndRig && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Segel och rigg
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.sailAndRig}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.electronicsAndNavigation && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Elektronik och navigationssystem
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.electronicsAndNavigation}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.hullAndDeck && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Skrov och däck
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.hullAndDeck}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.rudderPropellerAndDrive && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Roder, propeller, och drev
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.rudderPropellerAndDrive}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.fuelSystem && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Bränslesystem
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.fuelSystem}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.batteryAndElectricalSystem && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Batteri och elsystem
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.batteryAndElectricalSystem}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.sparePartsAndReplacements && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Reservdelar/byten
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.sparePartsAndReplacements}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.recommendedActions && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Rekommenderade åtgärder
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.recommendedActions}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.costAndTime && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Kostnad och tid
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.costAndTime}
                          </Typography>
                        </Grid>
                      )}
                      {serviceLog.comments && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Kommentarer/Anmärkningar
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {serviceLog.comments}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                    {(() => {
                      // Handle images - can be array or string that needs parsing
                      let images = serviceLog.images;
                      if (typeof images === 'string') {
                        try {
                          images = JSON.parse(images);
                        } catch (e) {
                          images = [];
                        }
                      }
                      return images && Array.isArray(images) && images.length > 0 && (
                        <>
                          <Divider sx={{ my: 3 }} />
                          <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                            Bilder ({images.length})
                          </Typography>
                          <Grid container spacing={2}>
                            {images.map((imageUrl, imgIndex) => (
                                <Grid item xs={12} sm={6} md={4} key={imgIndex}>
                                  <Box
                                    component="img"
                                    src={getImageDisplayUrl(imageUrl)}
                                    alt={`Bild ${imgIndex + 1}`}
                                    sx={{
                                      width: '100%',
                                      height: 'auto',
                                      maxHeight: '300px',
                                      objectFit: 'contain',
                                      display: 'block',
                                    }}
                                  />
                                </Grid>
                            ))}
                          </Grid>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        )}
      </Box>
    </LogTheme>
  );
};

export default ServiceLogs;